import { groupByDay } from '../../utils/dateGrouping';
import { calendarDaysUntil, formatCalendarDate, isAnchoredDaySpelling } from '../../utils/dateUtils';
import { COGGINS_NO_EXPIRY_LABEL, isCogginsName } from '../../utils/coggins';

/*
 * The patient chart's date KINDS, decided once for BOTH layouts.
 *
 * HorseProfileClassic and HorseProfileModern are near-duplicates (~1,300 lines
 * of shared data code) and their date handling had already diverged once. The
 * three decisions below are the ones that are not a one-line helper swap, so
 * they live here where the two layouts cannot drift apart again, and where a
 * unit test can pin them without mounting a 2,500-line component.
 *
 * Which KIND each column holds is looked up, never guessed from its name —
 * Docs/architecture/DATE-KINDS.md §1 is the table, and the INVERSE trap in
 * .claude/rules/data-model-landmines.md is why the two kinds need OPPOSITE
 * treatment.
 */

/** The shape both layouts already consume for a medication day-bucket. */
export interface MedicationDayGroup<T> {
  /** The CALENDAR day, `yyyy-MM-dd`. Also the timeline row id's suffix. */
  date: string;
  medications: T[];
}

/**
 * Bucket medications by the CALENDAR DAY of `Medication.startDate`.
 *
 * The bucket key is not a display string — it is a Map key that decides which
 * DAY of the clinical timeline a medication is filed under, and in the modern
 * layout it is the row's id (`medication-group-<day>`). The key used to be
 * `format(new Date(startDate), 'yyyy-MM-dd')`, read in the BROWSER'S zone, so a
 * barn-ops row stored at UTC midnight was filed under the PREVIOUS day at any
 * negative offset (every US clinic), and nothing on screen said so. DATE-KINDS
 * §2.2 names these four sites and says: the date PART, not `formatCalendarDate`.
 *
 * `Medication.startDate` is MIXED at the column level (§1.2 — the SOAP
 * auto-prescribe writer stamps an instant into it, W6), but the path that
 * reaches this chart is the medication dialog / barn ops one, whose value means
 * a day. Groups come back newest day first; input order is kept within a day
 * (the caller sorts first, and both layouts pass newest-first lists); a row
 * with no parseable start date is dropped rather than crashing the memo the way
 * `format(new Date(undefined))` did.
 */
export function groupMedicationsByStartDay<T extends { startDate?: string | null }>(
  medications: T[],
): MedicationDayGroup<T>[] {
  return groupByDay(medications, (m) => m.startDate, { kind: 'calendar' }).map(
    ({ dayKey, items }) => ({ date: dayKey, medications: items }),
  );
}

/**
 * The Coggins / EIA chip in the modern identity popover — and, since the lab
 * arm landed, the label BOTH chart layouts print for a Coggins expiry, from
 * either source (`Vaccination.nextDueDate` or `LabResult.expiryDate`).
 *
 * Both are CALENDAR days (DATE-KINDS §1.1; the lab one is a Postgres `date`, so
 * it reads back at UTC midnight), so the label prints the date PART and reads
 * the same in every browser zone. The old
 * `format(new Date(nextDueDate), 'MM/dd/yy')` printed the previous day at any
 * negative offset — an expiry a day early on the one document a show gate asks
 * to see.
 *
 * ⚠ THE OVERDUE FLAG IS A CALENDAR COMPARISON AS OF 2026-09-08, AND THE REASON
 * IT WAS NOT IS WORTH KEEPING. It used to be `new Date(nextDueDate) < now` — an
 * INSTANT compare — held that way deliberately so this chip could not disagree
 * with `PatientHeaderCard`'s badge, which used the same spelling. That premise
 * ended when the badge moved to `calendarDaysUntil` for the five-state Coggins
 * rendering, and keeping the instant compare here would have recreated the
 * disagreement from the other side: on the expiry day itself the chip would say
 * "Expired" while the header said "0d left".
 *
 * It was also WRONG on its own terms, in the way no ratchet can see — the
 * comparison class, not the render class. A day stored at UTC midnight is
 * already "< now" from 8 PM Eastern the PREVIOUS evening, so every US clinic saw
 * a Coggins flip to Expired most of a day early. A calendar day is valid
 * THROUGH the day it names: the test is `calendarDaysUntil(...) < 0`, and 0 —
 * today — is still current.
 *
 * Scope check before changing it: `cogginsExpiryLabel` has exactly one caller
 * (HorseProfileModern's chip), so this moved the Coggins surfaces and nothing
 * else. The overdue-VACCINATION count followed later, with its own population
 * rule (`overdueVaccinationCount` below, INVR164).
 *
 * `timeZone` is the CLINIC's (`useClinicTimezone()`): an expiry lapses at the
 * clinic's midnight, not the viewer's (DATE-KINDS §2.9). `now` is a parameter
 * only so the flip can be pinned in a test.
 *
 * ⚠ RETURNS A STATE, NOT JUST A BOOLEAN, and that is load-bearing. `isOverdue`
 * has only two values, so a caller that colours on it can render exactly two
 * colours — and the THIRD state, "on file, expiry unknown", is not-overdue.
 * HorseProfileModern coloured `isOverdue ? red : green` and therefore painted
 * every undated Coggins GREEN, i.e. "fine", for the entire pre-existing
 * population (the column is nullable with no backfill, so every row that
 * predates this feature is NULL). The memo feeding that chip carried a comment
 * promising the opposite — "never a green 'on file', which would read as 'fine'
 * for a row nobody has dated". `state` is what makes that promise checkable, and
 * it carries the same 30/60-day bands `PatientHeaderCard`'s badge already uses so
 * the two chart layouts cannot tell a clinician different things about one horse.
 * `isOverdue` is kept because it still answers its own narrower question.
 */
export type CogginsExpiryState = 'unknown' | 'current' | 'soon' | 'urgent' | 'expired' | 'positive';

/** One Coggins record as the chart header sees it - from either source (ADR-0019 D4 v2). */
export interface CogginsHeaderCandidate {
  /** Vaccination.dateAdministered or LabResult.reportDate - the EVENT day, newest wins. */
  when: string;
  /** Vaccination.nextDueDate or LabResult.expiryDate - a calendar day, or null when nobody recorded one. */
  expiry: string | null;
  accession: string | null;
  attachmentId: string | null;
  /** The stored result says carrier. Dominates every date: a positive horse is never "current". */
  positive: boolean;
}

/**
 * What the chart header says about a horse's Coggins, from the union of the
 * vaccination-path and lab-result-path candidates.
 *
 *  - IDENTITY (number, certificate) comes from the NEWEST record, full stop.
 *  - STATUS comes from the newest record that HAS a known expiry, but an older
 *    record's expiry is only borrowed while it is still in the future. A
 *    still-valid known expiry can never overstate coverage (the horse is covered
 *    at least until then), whereas an older, already-past expiry painted red next
 *    to a newer test that most likely renewed it would contradict the number on
 *    the same chip - that case reads as the grey "expiry unknown" instead.
 *
 * Found on release review 2026-09-09: with plain newest-wins, a horse with a dated
 * Coggins vaccination and a newer IDEXX result (expiry NULL, no backfill) lost its
 * green chip to grey on deploy day.
 */
export function pickCogginsSummary(
  candidates: CogginsHeaderCandidate[],
  timeZone: string,
  now: number = Date.now(),
): CogginsHeaderCandidate | null {
  if (candidates.length === 0) return null;
  const sorted = [...candidates].sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
  const newest = sorted[0];
  // A positive newest record is the whole story; no expiry is borrowed or shown as coverage.
  if (newest.positive || newest.expiry) return { ...newest };
  const stillValid = sorted.find((c) => c.expiry && (calendarDaysUntil(c.expiry, timeZone, now) ?? -1) >= 0);
  return { ...newest, expiry: stillValid?.expiry ?? null };
}

/**
 * The header's Coggins status for a picked candidate - ONE rule for both
 * layouts. Positive dominates: the date on a positive result is when the test
 * was valid to, not when the horse becomes safe, so it is shown as context
 * beside the word, never as a countdown.
 */
export function cogginsHeaderStatus(
  c: CogginsHeaderCandidate,
  timeZone: string,
  now: number = Date.now(),
): { label: string; isOverdue: boolean; state: CogginsExpiryState } {
  if (c.positive) {
    const day = c.expiry ? formatCalendarDate(c.expiry, 'MM/dd/yy') : null;
    return { label: day ? `Positive · ${day}` : 'Positive', isOverdue: true, state: 'positive' };
  }
  if (c.expiry) return cogginsExpiryLabel(c.expiry, timeZone, now);
  return { label: COGGINS_NO_EXPIRY_LABEL, isOverdue: false, state: 'unknown' };
}

export function cogginsExpiryLabel(
  nextDueDate: string,
  timeZone: string,
  now: number = Date.now(),
): { label: string; isOverdue: boolean; state: CogginsExpiryState } {
  const day = formatCalendarDate(nextDueDate, 'MM/dd/yy');
  // An unparseable due date used to throw out of the memo (RangeError from
  // date-fns) and blank the whole identity header. Treat it as an expiry we
  // cannot state - which is the SAME fact as a null expiry, so it gets the same
  // string (`COGGINS_NO_EXPIRY_LABEL`) rather than a second wording for one
  // meaning. It said a bare "On file" until 2026-09-08; that reads as "fine",
  // and the row it describes has no usable expiry at all.
  if (!day) return { label: COGGINS_NO_EXPIRY_LABEL, isOverdue: false, state: 'unknown' };
  const days = calendarDaysUntil(nextDueDate, timeZone, now) ?? 0;
  const isOverdue = days < 0;
  // Same thresholds as PatientHeaderCard's CogginsBadge (<30 red, 30-60 amber,
  // >60 green) so one horse reads the same in both chart layouts.
  const state: CogginsExpiryState = isOverdue
    ? 'expired'
    : days < 30
      ? 'urgent'
      : days <= 60
        ? 'soon'
        : 'current';
  return { label: `${isOverdue ? 'Expired' : 'Expires'} ${day}`, isOverdue, state };
}

/** What the vaccine verdicts read from a `Vaccination` row. */
export interface VaccinationDueRow {
  vaccineName?: string | null;
  dateAdministered?: string | null;
  nextDueDate?: string | null;
  isArchived?: boolean | null;
}

/**
 * The newest live record of each vaccine, which alone says when it is next due: the recall
 * reminders' rule (same name ignoring case, newest `dateAdministered`), so a booster supersedes
 * last year's due day. Coggins has its own chip in both layouts.
 */
function currentVaccinations<T extends VaccinationDueRow>(vaccinations: T[]): T[] {
  const administeredAt = (v: T) => Date.parse(v.dateAdministered ?? '') || -Infinity;
  const newest = new Map<string, T>();
  for (const v of vaccinations) {
    if (v.isArchived || !v.vaccineName || isCogginsName(v.vaccineName)) continue;
    const key = v.vaccineName.trim().toLowerCase();
    const held = newest.get(key);
    if (!held || administeredAt(v) > administeredAt(held)) newest.set(key, v);
  }
  return Array.from(newest.values());
}

/** Days ahead a vaccine reads "Due soon" (spec §16's default window). */
export const VACCINE_DUE_SOON_DAYS = 30;

export type VaccineStatus = 'OVERDUE' | 'DUE_SOON' | 'CURRENT' | 'NO_BOOSTER';

const STATUS_ORDER: Record<VaccineStatus, number> = { OVERDUE: 0, DUE_SOON: 1, CURRENT: 2, NO_BOOSTER: 3 };

export interface VaccineStanding {
  name: string;
  status: VaccineStatus;
  nextDueDate: string | null;
  lastGiven: string | null;
  /** The dose the standing comes from; every older live dose of the vaccine is superseded. */
  newestId?: string;
}

/**
 * Each vaccine's standing from its newest live dose (the tab's cards and row colours), worst first. Due is a
 * CALENDAR day counted from the clinic's today, one number for every verdict (DATE-KINDS §2.9); a newest dose
 * with no next-due day needs no booster.
 */
export function vaccineStandings(
  vaccinations: Array<VaccinationDueRow & { id?: string }>,
  timeZone: string,
  now: number = Date.now(),
): VaccineStanding[] {
  return currentVaccinations(vaccinations)
    .map((v) => {
      const days = calendarDaysUntil(v.nextDueDate, timeZone, now);
      const status: VaccineStatus =
        days === null ? 'NO_BOOSTER' : days < 0 ? 'OVERDUE' : days <= VACCINE_DUE_SOON_DAYS ? 'DUE_SOON' : 'CURRENT';
      return {
        name: v.vaccineName!.trim(),
        status,
        nextDueDate: v.nextDueDate ?? null,
        lastGiven: v.dateAdministered ?? null,
        newestId: v.id,
      };
    })
    .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name));
}

/** The header badge's counts, from the same standings as the tab so the two cannot disagree. */
export function vaccinationSummary(
  vaccinations: Array<VaccinationDueRow & { id?: string }>,
  timeZone: string,
  now: number = Date.now(),
): { overdue: number; dueSoon: number; current: number; noBooster: number; total: number } {
  const standings = vaccineStandings(vaccinations, timeZone, now);
  const count = (status: VaccineStatus) => standings.filter((s) => s.status === status).length;
  return {
    overdue: count('OVERDUE'),
    dueSoon: count('DUE_SOON'),
    current: count('CURRENT'),
    noBooster: count('NO_BOOSTER'),
    total: standings.length,
  };
}

/** How many vaccines are past due (Modern's quick-action strip), counted from the clinic's today (DATE-KINDS §2.9). */
export function overdueVaccinationCount(
  vaccinations: VaccinationDueRow[],
  timeZone: string,
  now: number = Date.now(),
): number {
  return currentVaccinations(vaccinations).filter(
    (v) => (calendarDaysUntil(v.nextDueDate, timeZone, now) ?? 0) < 0,
  ).length;
}

/**
 * The per-ROW rule for `MedicalRecord.date`, which is MIXED (DATE-KINDS §1.2):
 * one column, several writers, and they do not agree on the kind.
 *
 * THE DISCRIMINATOR IS THE EXACT UTC TIME-OF-DAY the value carries. The three
 * live writers:
 *
 *   web, this chart's own diagnostic dialog  `LabsDiagnosticsPanel.tsx:541,555,569`
 *     posts `new Date(yyyy-MM-dd).toISOString()` = `T00:00:00.000Z` — a picked
 *     DAY from a `type="date"` input, on the UTC-midnight anchor.
 *   web, barn ops  `Barns/BarnVisitWorkspace.tsx:367` posts `ymdToNoonUtcIso(...)`
 *     = `T12:00:00.000Z` — a picked DAY on the sanctioned noon anchor.
 *   iOS  `MedicalRecordFormView.swift:71` offers a `[.date, .hourAndMinute]`
 *     picker and `APIEndpoint.swift:36` encodes it `.iso8601` — a real MOMENT,
 *     which takes the instant arm by not landing on either anchor.
 *
 * That is why this stopped being UNCERTAIN. The old rule asked which TYPE a row
 * was, decided the web and iOS "disagree" on every non-visit-note type, and
 * parked them all on the legacy browser-zone render. But the disagreement is
 * between ROWS, not types, and the day-writing rows announce themselves — the
 * same negative result `LabResult.reportDate` produced (DATE-KINDS §0):
 * provenance and type say who wrote a value, the anchor says what it means.
 *
 * ⚠⚠ THE MILLISECOND IS NOT THE DISCRIMINATOR, AND THIS FILE SAID IT WAS FOR
 * ONE DAY (2026-09-04, corrected the same day). The claim was that Swift's
 * `.iso8601` emits no fractional seconds while a browser `toISOString()` always
 * emits `.000`, so `T00:00:00Z` and `T00:00:00.000Z` are one character apart and
 * land on opposite arms. That is true of the bytes iOS POSTS and false of the
 * bytes this component READS. `MedicalRecord.date` is a Prisma `DateTime`;
 * `medical-records.service.ts` returns the Prisma object and the controller
 * hands it to `res.json`, so every GET re-serialises through
 * `Date.prototype.toJSON` → `toISOString()` and carries `.000` no matter what
 * the client sent. `T00:00:00Z` is unreachable here. (The mobile repo pins the
 * same fact: `StableTrackTests/DateKindsTests.swift:121-124`.)
 *
 * ⚠ SO THIS RULE HAS A STATED RESIDUAL, and it is accepted deliberately. An iOS
 * moment that lands on exactly 00:00:00 or 12:00:00 UTC — whole hour, whole
 * minute, whole second — reads as a DAY and renders a day late from its date
 * part. Those are 8 PM and 8 AM Eastern (7 PM / 7 AM in EST), and the equivalent
 * pair in every whole-hour-offset zone. It is not negligible for this column
 * specifically: the iOS picker offers hour-and-minute, and 8:00 PM is a round
 * time a human chooses. The trade is still right way round — the DAY population
 * is misrendered on EVERY view without this rule (a picked Apr 29 reads "Apr 28"
 * for every negative-offset clinic, i.e. every clinic we have), the INSTANT
 * population only on two exact minutes of the day. Narrowing further is not
 * available: iOS writes the SAME three types the web day-dialog writes
 * (`MedicalRecordType.allCases`), so neither the type nor the
 * `types=diagnostic,imaging,outside_lab` feed filter separates the writers. The
 * real close is on the WRITE side — DATE-KINDS §2.5 W8, give iOS a day-anchored
 * post — and until then this residual stands, named.
 *
 * ⚠ THE TYPE OVERRIDE SURVIVES, AND IT IS NOT DECORATION — it is the same
 * residual made unacceptable by frequency. A `visit_note` is an INSTANT on every
 * surface (the web posts `formData.dateTime.toISOString()` from a
 * `DateTimePicker` — `VisitNote/VisitNoteDialog.tsx:67` — and iOS uses the same
 * `[.date, .hourAndMinute]` picker). A visit note recorded at 8:00 PM EDT IS
 * exactly `T00:00:00.000Z`, and the noon arm collides the same way at 8:00 AM
 * EDT. For a field a clinician types a round hour into all day long that is not
 * a coincidence rate, so the type is excluded FIRST.
 */
export type MedicalRecordDateKind = 'instant' | 'day';

export function medicalRecordDateKind(
  type: string | null | undefined,
  value: string | Date | null | undefined,
): MedicalRecordDateKind {
  if (type === 'visit_note' || type === 'Visit Note') return 'instant';
  return isAnchoredDaySpelling(value) ? 'day' : 'instant';
}
